/**
 * Graphviz DOT (de)serialiser for the Graph Builder.
 *
 * Export writes the current Mission (the set of nodes reachable from its
 * root) as a `digraph`. Standard Graphviz attributes (label/shape/color)
 * are emitted so the file renders in any DOT viewer, while every Cybercore
 * field rides along under `cc_*` attributes so an export -> import round
 * trip is lossless. Edges run parent -> child (multi-parent DAGs included).
 *
 * These are pure functions: no Firestore, no store. The store wires them to
 * node CRUD (see exportCurrentGraphDot / importGraphFromDot).
 */
import type { GNode, NodeImage } from '~/models/graphNode';

export interface DotNode {
	id: string;
	attrs: Record<string, string>;
}
export interface DotEdge {
	from: string;
	to: string;
}
export interface ParsedDot {
	name: string;
	nodes: DotNode[];
	edges: DotEdge[];
}

// Fields lifted out of a parsed DOT node, ready to apply onto a GNode.
export interface DotNodeFields {
	title: string;
	shape: string;
	color: string;
	ident: string;
	type: string;
	subType: string;
	glyph: string;
	glyphColor: string | null;
	tags: string[];
	info: string;
	readAloud: string;
	environment: string;
	images: NodeImage[];
	x: number | null;
	y: number | null;
	isRoot: boolean;
}

// ─── Escaping ──────────────────────────────────────────────────────────
// Values are emitted as quoted DOT strings on a single line. We escape the
// backslash + quote (DOT's own escapes) plus whitespace that would break
// the one-statement-per-line layout, and reverse it on parse. The result is
// still valid DOT (`\"`-escaped quoted strings).
function esc(v: string): string {
	return v
		.replace(/\\/g, '\\\\')
		.replace(/"/g, '\\"')
		.replace(/\r/g, '\\r')
		.replace(/\n/g, '\\n')
		.replace(/\t/g, '\\t');
}
function unesc(v: string): string {
	return v.replace(/\\(.)/g, (_, c: string) =>
		c === 'n' ? '\n' : c === 'r' ? '\r' : c === 't' ? '\t' : c
	);
}

const q = (id: string): string => `"${esc(id)}"`;
const attr = (k: string, v: string): string => `${k}="${esc(v)}"`;

// ─── Export ────────────────────────────────────────────────────────────
export function graphToDot(name: string, nodes: GNode[], rootId: string): string {
	const present = new Set(nodes.map((n) => n.id));
	const lines: string[] = [];
	lines.push('// Cybercore Graph Builder export (Graphviz DOT).');
	lines.push('// cc_* attributes carry Cybercore fields for lossless re-import.');
	lines.push(`digraph ${q(name || 'Mission')} {`);
	lines.push('  rankdir=TB;');
	lines.push('  node [style=filled, fillcolor="#0c0c10", fontname="sans-serif"];');

	for (const n of nodes) {
		const parts: string[] = [attr('label', n.title || n.id)];
		if (n.shape) parts.push(attr('shape', n.shape));
		if (n.color) parts.push(attr('color', n.color), attr('fontcolor', n.color));
		if (n.ident) parts.push(attr('cc_ident', n.ident));
		if (n.type) parts.push(attr('cc_type', n.type));
		if (n.subType) parts.push(attr('cc_subtype', n.subType));
		if (n.glyph) parts.push(attr('cc_glyph', n.glyph));
		if (n.glyphColor) parts.push(attr('cc_glyphcolor', n.glyphColor));
		if (n.tags?.length) parts.push(attr('cc_tags', JSON.stringify(n.tags)));
		if (n.info) parts.push(attr('cc_info', n.info));
		if (n.readAloud) parts.push(attr('cc_readaloud', n.readAloud));
		if (n.environment) parts.push(attr('cc_environment', n.environment));
		if (n.images?.length) parts.push(attr('cc_images', JSON.stringify(n.images)));
		if (n.x != null) parts.push(attr('cc_x', String(n.x)));
		if (n.y != null) parts.push(attr('cc_y', String(n.y)));
		if (n.id === rootId) parts.push('cc_root="true"');
		lines.push(`  ${q(n.id)} [${parts.join(', ')}];`);
	}

	const seen = new Set<string>();
	for (const n of nodes) {
		for (const childId of n.children ?? []) {
			if (!present.has(childId)) continue; // edge leaves the mission
			const key = `${n.id} ${childId}`;
			if (seen.has(key)) continue;
			seen.add(key);
			lines.push(`  ${q(n.id)} -> ${q(childId)};`);
		}
	}

	lines.push('}');
	return lines.join('\n');
}

// ─── Parse ─────────────────────────────────────────────────────────────
// A pragmatic parser for the subset we emit, lenient enough for typical
// hand- or tool-written DOT: it reads the graph name, node statements with
// attribute lists, and `a -> b -> c` edge chains. Subgraphs, ports and the
// `graph`/`node`/`edge` default statements are ignored.
export function parseDot(text: string): ParsedDot {
	const src = stripComments(text);
	const open = src.indexOf('{');
	const close = src.lastIndexOf('}');
	if (open === -1 || close === -1 || close < open) {
		throw new Error('Not a DOT graph: missing { } body.');
	}
	const header = src.slice(0, open);
	const body = src.slice(open + 1, close);

	const nameMatch = header.match(/(?:strict\s+)?(?:di)?graph\s+("(?:[^"\\]|\\.)*"|[A-Za-z0-9_.]+)?\s*$/i);
	let name = '';
	if (nameMatch?.[1]) {
		name = nameMatch[1].startsWith('"')
			? unesc(nameMatch[1].slice(1, -1))
			: nameMatch[1];
	}

	const nodes: DotNode[] = [];
	const nodeIndex = new Map<string, DotNode>();
	const edges: DotEdge[] = [];
	const reserved = new Set(['node', 'edge', 'graph', 'subgraph']);

	const ensureNode = (id: string): DotNode => {
		let n = nodeIndex.get(id);
		if (!n) {
			n = { id, attrs: {} };
			nodeIndex.set(id, n);
			nodes.push(n);
		}
		return n;
	};

	for (const stmt of splitStatements(body)) {
		const s = stmt.trim();
		if (!s) continue;
		if (containsTopLevelArrow(s)) {
			const ids = parseEdgeChain(s);
			for (let i = 0; i + 1 < ids.length; i++) {
				edges.push({ from: ids[i], to: ids[i + 1] });
				ensureNode(ids[i]);
				ensureNode(ids[i + 1]);
			}
			continue;
		}
		const first = readId(s, 0);
		if (!first) continue;
		if (reserved.has(first.id.toLowerCase())) continue; // default statement
		const rest = s.slice(first.next).trimStart();
		if (rest.startsWith('=')) continue; // graph-level `key=value`
		const node = ensureNode(first.id);
		if (rest.startsWith('[')) {
			Object.assign(node.attrs, parseAttrList(rest));
		}
	}

	return { name, nodes, edges };
}

// Pull the round-trip fields out of a parsed node's attribute bag.
export function dotNodeFields(attrs: Record<string, string>): DotNodeFields {
	const a = (k: string): string => attrs[k] ?? '';
	const num = (k: string): number | null => {
		const v = attrs[k];
		if (v === undefined || v === '') return null;
		const n = Number(v);
		return Number.isFinite(n) ? n : null;
	};
	let tags: string[] = [];
	if (attrs.cc_tags) {
		try {
			const parsed = JSON.parse(attrs.cc_tags);
			if (Array.isArray(parsed)) tags = parsed.map(String);
		} catch {
			tags = attrs.cc_tags.split(',').map((t) => t.trim()).filter(Boolean);
		}
	}
	let images: NodeImage[] = [];
	if (attrs.cc_images) {
		try {
			const parsed = JSON.parse(attrs.cc_images);
			if (Array.isArray(parsed)) {
				images = parsed
					.filter((i) => i && typeof i === 'object')
					.map((i) => ({ prompt: String(i.prompt ?? ''), url: String(i.url ?? '') }));
			}
		} catch {
			images = [];
		}
	}
	return {
		title: a('label') || a('cc_title'),
		shape: a('shape'),
		color: a('color'),
		ident: a('cc_ident'),
		type: a('cc_type'),
		subType: a('cc_subtype'),
		glyph: a('cc_glyph'),
		glyphColor: attrs.cc_glyphcolor || null,
		tags,
		info: a('cc_info'),
		readAloud: a('cc_readaloud'),
		environment: a('cc_environment'),
		images,
		x: num('cc_x'),
		y: num('cc_y'),
		isRoot: /^(true|1|yes)$/i.test(a('cc_root')),
	};
}

// ─── Low-level scanners ────────────────────────────────────────────────
function stripComments(text: string): string {
	let out = '';
	let i = 0;
	let inStr = false;
	while (i < text.length) {
		const ch = text[i];
		if (inStr) {
			out += ch;
			if (ch === '\\') {
				out += text[i + 1] ?? '';
				i += 2;
				continue;
			}
			if (ch === '"') inStr = false;
			i++;
			continue;
		}
		if (ch === '"') {
			inStr = true;
			out += ch;
			i++;
			continue;
		}
		if (ch === '/' && text[i + 1] === '/') {
			while (i < text.length && text[i] !== '\n') i++;
			continue;
		}
		if (ch === '#') {
			while (i < text.length && text[i] !== '\n') i++;
			continue;
		}
		if (ch === '/' && text[i + 1] === '*') {
			i += 2;
			while (i < text.length && !(text[i] === '*' && text[i + 1] === '/')) i++;
			i += 2;
			continue;
		}
		out += ch;
		i++;
	}
	return out;
}

// Split the body into statements on top-level `;` or newline (outside quotes
// and outside [...] / {...}).
function splitStatements(body: string): string[] {
	const stmts: string[] = [];
	let depth = 0;
	let inStr = false;
	let cur = '';
	for (let i = 0; i < body.length; i++) {
		const ch = body[i];
		if (inStr) {
			cur += ch;
			if (ch === '\\') {
				cur += body[i + 1] ?? '';
				i++;
				continue;
			}
			if (ch === '"') inStr = false;
			continue;
		}
		if (ch === '"') {
			inStr = true;
			cur += ch;
			continue;
		}
		if (ch === '[' || ch === '{') depth++;
		else if (ch === ']' || ch === '}') depth = Math.max(0, depth - 1);
		if ((ch === ';' || ch === '\n') && depth === 0) {
			stmts.push(cur);
			cur = '';
			continue;
		}
		cur += ch;
	}
	if (cur.trim()) stmts.push(cur);
	return stmts;
}

function containsTopLevelArrow(s: string): boolean {
	let inStr = false;
	let depth = 0;
	for (let i = 0; i < s.length; i++) {
		const ch = s[i];
		if (inStr) {
			if (ch === '\\') {
				i++;
				continue;
			}
			if (ch === '"') inStr = false;
			continue;
		}
		if (ch === '"') {
			inStr = true;
			continue;
		}
		if (ch === '[') depth++;
		else if (ch === ']') depth = Math.max(0, depth - 1);
		else if (depth === 0 && ch === '-' && (s[i + 1] === '>' || s[i + 1] === '-')) return true;
	}
	return false;
}

// `a -> b -> c [attrs]` -> ['a','b','c']. Trailing attribute lists are dropped.
function parseEdgeChain(s: string): string[] {
	const ids: string[] = [];
	let i = 0;
	while (i < s.length) {
		const tok = readId(s, i);
		if (!tok) break;
		ids.push(tok.id);
		i = tok.next;
		// skip to the next `->` / `--`, bailing at an attribute list
		while (i < s.length && /\s/.test(s[i])) i++;
		if (s[i] === '[') break;
		if (s[i] === '-' && (s[i + 1] === '>' || s[i + 1] === '-')) {
			i += 2;
			continue;
		}
		break;
	}
	return ids;
}

// Read an ID token (quoted string or bare word) at position i, unescaping a
// quoted value. Returns null if none.
function readId(s: string, i: number): { id: string; next: number } | null {
	while (i < s.length && /\s/.test(s[i])) i++;
	if (i >= s.length) return null;
	if (s[i] === '"') {
		let j = i + 1;
		let raw = '';
		while (j < s.length) {
			const ch = s[j];
			if (ch === '\\') {
				raw += ch + (s[j + 1] ?? '');
				j += 2;
				continue;
			}
			if (ch === '"') {
				j++;
				break;
			}
			raw += ch;
			j++;
		}
		return { id: unesc(raw), next: j };
	}
	let j = i;
	while (j < s.length && /[A-Za-z0-9_.]/.test(s[j])) j++;
	if (j === i) return null;
	return { id: s.slice(i, j), next: j };
}

// Parse `[ key=value, key2="v2" ... ]` into a record (keys lowercased).
function parseAttrList(s: string): Record<string, string> {
	const out: Record<string, string> = {};
	const start = s.indexOf('[');
	const end = s.lastIndexOf(']');
	if (start === -1 || end === -1 || end < start) return out;
	const inner = s.slice(start + 1, end);
	let i = 0;
	while (i < inner.length) {
		const keyTok = readId(inner, i);
		if (!keyTok) break;
		i = keyTok.next;
		while (i < inner.length && /\s/.test(inner[i])) i++;
		if (inner[i] !== '=') {
			// bare attribute (no value); skip separators and continue
			while (i < inner.length && /[\s,]/.test(inner[i])) i++;
			out[keyTok.id.toLowerCase()] = 'true';
			continue;
		}
		i++; // skip '='
		const valTok = readId(inner, i);
		if (!valTok) break;
		out[keyTok.id.toLowerCase()] = valTok.id;
		i = valTok.next;
		while (i < inner.length && /[\s,]/.test(inner[i])) i++;
	}
	return out;
}
